import React, { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function LoginForm() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSignup, setShowSignup] = useState(false);
  const [allowNewUsers, setAllowNewUsers] = useState(true); // Default to true for better UX
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  const { login, register } = useAuth();

  // Fetch registration settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch("/api/auth/settings");
        if (response.ok) {
          const data = await response.json();
          setAllowNewUsers(data.allowNewUsers);
        }
      } catch (error) {
        console.error("Failed to fetch settings:", error);
        // Keep default value (true) if fetch fails
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const result = showSignup
        ? await register(username.trim(), password)
        : await login(username.trim(), password);

      if (!result.success) {
        setError(result.message);
      }
      // On success, the AuthContext will update and redirect automatically
    } catch (error) {
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    // Only allow switching to signup if registration is enabled
    if (!showSignup && !allowNewUsers) {
      return;
    }
    setShowSignup(!showSignup);
    setError("");
    setUsername("");
    setPassword("");
  };

  // Show loading state while fetching settings
  if (isLoadingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            {showSignup ? "Create Account" : "Welcome to RGPV"}
          </CardTitle>
          <CardDescription>
            {showSignup
              ? "Sign up to start managing your media collection"
              : "Sign in to access your media collection"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete={showSignup ? "new-password" : "current-password"}
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading
                ? "Please wait..."
                : showSignup
                ? "Sign Up"
                : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            {allowNewUsers ? (
              <Button
                variant="link"
                onClick={toggleMode}
                disabled={isLoading}
                className="text-sm"
              >
                {showSignup
                  ? "Already have an account? Sign in"
                  : "Don't have an account? Sign up"}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Registration is currently disabled. Please contact an
                administrator.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default LoginForm;
